import clsx from "clsx";
import { FunctionComponent } from "react";

import { ArticleLayout } from "@ui/components/ledes/ledes.types";
import SELECTORS from "@ui/config/class-selectors.config";
import { useComponentConfig } from "@ui/context/component-config-context";
import useDefaults from "@ui/hooks/use-defaults";

import ShareButtonsCopyLink from "./components/ShareButtonsCopyLink";
import ShareButtonsFacebook from "./components/ShareButtonsFacebook";
import ShareButtonsGiftLink from "./components/ShareButtonsGiftLink";
import ShareButtonsRSS from "./components/ShareButtonsRSS";
import ShareButtonsSharePopover, {
  type PopoverButtons,
} from "./components/ShareButtonsSharePopover";
import ShareButtonsThreads from "./components/ShareButtonsThreads";
import { container } from "./ShareButtons.css";

type Buttons =
  | "facebook"
  | "copy"
  | "rss"
  | "threads"
  | "giftLink"
  | "sharePopover"
  | "preferredGoogleSource";

export interface ShareButtonsBaseConfig {
  buttons: Buttons[];
  popoverButtons: PopoverButtons[];
  popoverClassname?: string;
  giftLinks: {
    subscriberMessaging: (giftCount: number | null) => React.ReactNode;
    nonSubscriberMessaging?: React.ReactNode;
    ctaText?: string;
    ctaLink?: string;
    signInText?: string;
  };
}

interface Props extends Partial<ShareButtonsBaseConfig> {
  url: string;
  title: string;
  rssUrl?: string;
  layout: ArticleLayout | "stream" | "video";
}

const ShareButtons: FunctionComponent<Props> = ({
  url,
  title,
  rssUrl,
  layout,
  ...props
}) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareButtonsConfig = useComponentConfig("shareButtons", "");

  const { buttons, popoverButtons, popoverClassname } = useDefaults(
    props,
    shareButtonsConfig,
  );

  return (
    <ul className={clsx(SELECTORS.ShareButtons, container)}>
      {buttons.map((buttonType) =>
        buttonType === "copy" ? (
          <li key={buttonType}>
            <ShareButtonsCopyLink url={url} layout={layout} />
          </li>
        ) : buttonType === "giftLink" ? (
          <li key={buttonType}>
            <ShareButtonsGiftLink url={url} layout={layout} />
          </li>
        ) : buttonType === "facebook" ? (
          <li key={buttonType}>
            <ShareButtonsFacebook
              url={encodedUrl}
              title={encodedTitle}
              layout={layout}
            />
          </li>
        ) : buttonType === "threads" ? (
          <li key={buttonType}>
            <ShareButtonsThreads
              url={encodedUrl}
              title={encodedTitle}
              layout={layout}
            />
          </li>
        ) : buttonType === "sharePopover" ? (
          <li key={buttonType}>
            <ShareButtonsSharePopover
              url={url}
              title={title}
              layout={layout}
              popoverButtons={popoverButtons}
              popoverClassname={popoverClassname}
            />
          </li>
        ) : rssUrl ? ( // buttonType === "rss"
          <li key={buttonType}>
            <ShareButtonsRSS url={rssUrl} layout={layout} />
          </li>
        ) : null,
      )}
    </ul>
  );
};

export default ShareButtons;
